import { useEffect } from 'react';
import Loader from '@lingoda/ui/feedback/Loader';
import {
    areSubscriptionsLoadedSelector,
    currentSubscriptionSelector,
} from '@lingoda/subscriptions';
import { usePurchaseQuery, useUserCreditsQuery } from '@lingoda/monolith-graphql';
import {
    changeSubscriptionFailedPath,
    extraCreditsNotAvailablePath,
    goTo,
    redirect,
    studentCreditsCheckoutPath,
    useRequiredParams,
    useRouteMatch,
} from '@lingoda/router';
import { assert } from '@lingoda/utils';
import { useSelector } from '@lingoda/hooks';
import Layout from './Layout';

const Checkout = () => {
    const { purchaseId } = useRequiredParams<{ purchaseId: string }>();
    const { loading, data } = usePurchaseQuery({
        variables: { purchaseId },
    });
    const { loading: userCreditsLoading, data: userCreditsData } = useUserCreditsQuery();

    const purchase = data?.purchase ?? undefined;

    const subscriptionsLoaded = useSelector(areSubscriptionsLoadedSelector);
    const subscription = useSelector(currentSubscriptionSelector);
    const isSubscriptionChange = Boolean(purchase?.isSubscription && subscription);

    useEffect(() => {
        // not a very likely scenario since this is checked in the useCheckProductEligible
        // but better to double check for security
        if (isSubscriptionChange && !subscription?.allowChange) {
            return goTo(changeSubscriptionFailedPath());
        }
    }, [subscription, isSubscriptionChange]);

    const isExtraCreditsCheckout = !!useRouteMatch(studentCreditsCheckoutPath.raw);

    useEffect(() => {
        if (
            isExtraCreditsCheckout &&
            userCreditsData?.studentCreditsInfo?.canBuyExtraCredits === false
        ) {
            redirect(extraCreditsNotAvailablePath());
        }
    }, [userCreditsData?.studentCreditsInfo?.canBuyExtraCredits, isExtraCreditsCheckout]);

    if (loading || !subscriptionsLoaded || userCreditsLoading) {
        return <Loader />;
    }

    assert(purchase, 'Invalid argument');

    return <Layout purchase={purchase} isSubscriptionChange={isSubscriptionChange} />;
};

export default Checkout;
